import React, { useMemo } from 'react';

import { Box, Chip, Stack, Typography } from '@mui/material';

import { type Dayjs, formatDateSlashes } from '~/services/TimeService';

import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { availabilityLabels, getAvailabilityChipColor } from '../surcharges/surchargeAvailability';
import { getUnmatchedCardSx } from '../surcharges/surchargeCardStyles';

import ManageRoomRateBlackoutDatesAction from './ManageRoomRateBlackoutDatesAction';
import {
  type RoomRateBlackoutAssignment,
  getBlackoutAvailability,
  groupRoomRateBlackoutAssignments,
} from './appliedBlackoutUtils';

interface Props {
  assignments: Array<RoomRateBlackoutAssignment>;
  now: Dayjs;
}

export default function UnmatchedBlackoutRoomRateCards({ assignments, now }: Props) {
  const roomGroups = useMemo(() => groupRoomRateBlackoutAssignments(assignments), [assignments]);

  if (assignments.length === 0) {
    return (
      <Box
        sx={(theme) => ({
          ...getUnmatchedCardSx(theme),
          color: 'text.secondary',
          py: 1.5,
          textAlign: 'center',
        })}
      >
        <Typography variant="body2">No room-rate blackout blocks without a matching Salesforce blackout.</Typography>
      </Box>
    );
  }

  return (
    <>
      {roomGroups.flatMap((roomGroup) =>
        roomGroup.rateGroups.map((rateGroup) => {
          const ratePlanLabel = [rateGroup.ratePlanName, rateGroup.ratePlanCode].filter(Boolean).join(' - ');

          return (
            <Box key={rateGroup.roomRateId} sx={getUnmatchedCardSx}>
              <Stack spacing={1.25}>
                <Stack
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                >
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    <Typography fontWeight={700} variant="body2">
                      {roomGroup.roomName}
                    </Typography>
                    <ManageRoomRateBlackoutDatesAction href={rateGroup.href} />
                  </Stack>
                  <Typography
                    component={rateGroup.href ? 'a' : 'span'}
                    {...(rateGroup.href ? { href: rateGroup.href, target: '_blank', rel: 'noreferrer' } : {})}
                    color={rateGroup.href ? 'primary' : 'text.secondary'}
                    variant="body2"
                  >
                    {ratePlanLabel}
                  </Typography>
                </Stack>
                <Stack spacing={0.75}>
                  {rateGroup.blackoutDates.map((roomBlackoutDate) => {
                    const availability = getBlackoutAvailability(roomBlackoutDate, now);

                    return (
                      <Stack
                        key={roomBlackoutDate.id}
                        alignItems={{ sm: 'center' }}
                        direction={{ sm: 'row' }}
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Typography variant="body2">
                          {formatDateSlashes(roomBlackoutDate.start_date)} to{' '}
                          {formatDateSlashes(roomBlackoutDate.end_date)} · {daysOfWeekMessage(roomBlackoutDate)}
                        </Typography>
                        <Chip
                          color={getAvailabilityChipColor(availability)}
                          label={availabilityLabels[availability]}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          );
        }),
      )}
    </>
  );
}
